/**
 * The state behind where the metrics page looks (TRA-1592): the window
 * end from the URL and one zoom for the whole page, dragged on any chart
 * and moved by the page's arrows. The maths is in metricsZoom and
 * metricsPan; this keeps the zoom and hands every card the same stretch.
 * The zoom never reaches the URL: a window pick, "Back to now" and "Reset
 * zoom" all clear it.
 */

import { useState } from 'react'

import { atEnd, hoursToLoad, shownRange, type PanDirection } from '@/components/models/home/replicaPan'
import type { TimeRange } from '@/components/models/home/replicaZoom'
import { MAX_WINDOW_HOURS } from '@/components/models/home/scalingWindow'
import type { ReplicaView } from '@/components/models/home/useReplicaView'
import { pageViewAt, type PageView } from '@/components/models/metrics/metricsPan'
import { SHARPEN_AFTER_MS, steppedPage } from '@/components/models/metrics/metricsZoom'
import { useDebounce } from '@/hooks/useDebounce'

export interface MetricsZoom {
  zoom: TimeRange | null
  /** The zoom once it has held still for SHARPEN_AFTER_MS: what the main cards read at full detail. */
  settled: TimeRange | null
  /** The stretch every card draws, or null for the whole window at now. */
  shown: TimeRange | null
  /** The stretch shown and where the arrows can go, from one clock read. */
  view: PageView
  /** The capacity chart's view: the page's stretch over the 7 days of replica history. */
  replicaView: ReplicaView
  setZoom: (zoom: TimeRange) => void
  resetZoom: () => void
  step: (direction: PanDirection) => void
  backToNow: () => void
}

/**
 * `end` is the window end the URL carries, null at now; `setEnd` writes it
 * back when a step leaves the loaded window.
 */
export function useMetricsZoom(
  hours: number,
  end: number | null,
  setEnd: (end: number | null) => void,
): MetricsZoom {
  const [zoom, setZoom] = useState<TimeRange | null>(null)
  const settled = useDebounce(zoom, SHARPEN_AFTER_MS)
  const state = { end, zoom }
  // Both arrows take their flags from one clock read (pageViewAt), so they agree.
  const view = pageViewAt(state, hours)
  const capacityHours = Math.min(hours, MAX_WINDOW_HOURS)

  function step(direction: PanDirection): void {
    const next = steppedPage(state, hours, direction)
    setZoom(next.zoom)
    if (next.end !== end) setEnd(next.end)
  }

  function resetZoom(): void {
    setZoom(null)
  }

  function backToNow(): void {
    setZoom(null)
    setEnd(null)
  }

  return {
    zoom,
    settled,
    shown: shownRange(state, hours),
    view,
    replicaView: {
      shown: zoom ?? shownRange(atEnd(end), capacityHours),
      loadHours: hoursToLoad(atEnd(end), capacityHours),
      zoomed: zoom !== null,
      moved: end !== null,
      canEarlier: view.canEarlier,
      canLater: view.canLater,
      zoom: setZoom,
      resetZoom,
      earlier: () => { step('earlier') },
      later: () => { step('later') },
      backToNow,
    },
    setZoom,
    resetZoom,
    step,
    backToNow,
  }
}
